import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { setNativeChromeColor } from '@/utils/nativeChrome';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available color schemes with their gradient colors
export const colorSchemes = {
  purple: {
    name: 'Purple',
    primary: '280 100% 70%',
    secondary: '320 100% 60%',
    accent: '300 100% 65%',
    gradientFrom: '#a855f7',
    gradientTo: '#ec4899',
  },
  orange: {
    name: 'Sunset',
    primary: '35 100% 55%',
    secondary: '25 100% 50%',
    accent: '45 100% 60%',
    gradientFrom: '#facc15',
    gradientTo: '#f97316',
  },
  blue: {
    name: 'Ocean',
    primary: '200 100% 50%',
    secondary: '220 100% 60%',
    accent: '190 100% 45%',
    gradientFrom: '#06b6d4',
    gradientTo: '#3b82f6',
  },
  green: {
    name: 'Forest',
    primary: '140 70% 45%',
    secondary: '160 70% 40%',
    accent: '120 60% 50%',
    gradientFrom: '#84cc16',
    gradientTo: '#22c55e',
  },
  red: {
    name: 'Cherry',
    primary: '350 90% 60%',
    secondary: '330 90% 55%',
    accent: '0 85% 60%',
    gradientFrom: '#fb7185',
    gradientTo: '#ef4444',
  },
  teal: {
    name: 'Mint',
    primary: '170 70% 45%',
    secondary: '180 60% 40%',
    accent: '160 65% 50%',
    gradientFrom: '#2dd4bf',
    gradientTo: '#14b8a6',
  },
  pink: {
    name: 'Bubblegum',
    primary: '330 90% 65%',
    secondary: '350 85% 60%',
    accent: '310 85% 60%',
    gradientFrom: '#f472b6',
    gradientTo: '#e879f9',
  },
  // NEW THEMES
  rainbow: {
    name: 'Rainbow',
    primary: '330 90% 65%',
    secondary: '200 100% 50%',
    accent: '45 100% 60%',
    gradientFrom: '#ff6b6b',
    gradientTo: '#4ecdc4',
  },
  gold: {
    name: 'Gold',
    primary: '45 90% 50%',
    secondary: '35 85% 45%',
    accent: '50 95% 55%',
    gradientFrom: '#ffd700',
    gradientTo: '#b8860b',
  },
  silver: {
    name: 'Silver',
    primary: '220 10% 70%',
    secondary: '220 5% 60%',
    accent: '220 15% 75%',
    gradientFrom: '#c0c0c0',
    gradientTo: '#808080',
  },
  copper: {
    name: 'Copper',
    primary: '25 70% 50%',
    secondary: '15 65% 45%',
    accent: '30 75% 55%',
    gradientFrom: '#b87333',
    gradientTo: '#da8a67',
  },
  neon: {
    name: 'Neon',
    primary: '180 100% 50%',
    secondary: '300 100% 60%',
    accent: '120 100% 50%',
    gradientFrom: '#00ffff',
    gradientTo: '#ff00ff',
  },
  ember: {
    name: 'Ember',
    primary: '15 90% 55%',
    secondary: '5 85% 50%',
    accent: '25 95% 60%',
    gradientFrom: '#ff4500',
    gradientTo: '#8b0000',
  },
  // CONFLICTING/BOLD MIXES
  electric: {
    name: 'Electric',
    primary: '270 100% 60%',
    secondary: '180 100% 50%',
    accent: '60 100% 50%',
    gradientFrom: '#8b5cf6',
    gradientTo: '#06ffa5',
  },
  vaporwave: {
    name: 'Vaporwave',
    primary: '300 80% 60%',
    secondary: '180 80% 50%',
    accent: '330 85% 65%',
    gradientFrom: '#ff71ce',
    gradientTo: '#01cdfe',
  },
  bloodmoon: {
    name: 'Blood Moon',
    primary: '0 80% 45%',
    secondary: '30 70% 40%',
    accent: '350 85% 50%',
    gradientFrom: '#8b0000',
    gradientTo: '#ff8c00',
  },
  aurora: {
    name: 'Aurora',
    primary: '160 80% 45%',
    secondary: '280 70% 55%',
    accent: '200 85% 50%',
    gradientFrom: '#00ff87',
    gradientTo: '#60efff',
  },
  twilight: {
    name: 'Twilight',
    primary: '260 60% 50%',
    secondary: '30 80% 55%',
    accent: '280 70% 60%',
    gradientFrom: '#5b21b6',
    gradientTo: '#f59e0b',
  },
  arctic: {
    name: 'Arctic',
    primary: '200 70% 60%',
    secondary: '180 60% 70%',
    accent: '210 80% 65%',
    gradientFrom: '#a5f3fc',
    gradientTo: '#0ea5e9',
  },
  lava: {
    name: 'Lava',
    primary: '20 95% 55%',
    secondary: '45 100% 50%',
    accent: '0 90% 50%',
    gradientFrom: '#ef4444',
    gradientTo: '#fbbf24',
  },
  cosmic: {
    name: 'Cosmic',
    primary: '270 90% 60%',
    secondary: '200 100% 50%',
    accent: '330 90% 65%',
    gradientFrom: '#7c3aed',
    gradientTo: '#06b6d4',
  },
  // FLAG THEMES
  jamaica: {
    name: 'Jamaica',
    primary: '50 100% 50%',
    secondary: '140 100% 30%',
    accent: '50 100% 45%',
    gradientFrom: '#ffd700',
    gradientTo: '#009b3a',
  },
  usa: {
    name: 'USA',
    primary: '0 80% 50%',
    secondary: '220 80% 45%',
    accent: '0 0% 100%',
    gradientFrom: '#b22234',
    gradientTo: '#3c3b6e',
  },
  uk: {
    name: 'UK',
    primary: '0 80% 45%',
    secondary: '220 70% 40%',
    accent: '0 0% 100%',
    gradientFrom: '#c8102e',
    gradientTo: '#012169',
  },
  nigeria: {
    name: 'Nigeria',
    primary: '140 70% 35%',
    secondary: '0 0% 95%',
    accent: '140 65% 40%',
    gradientFrom: '#008751',
    gradientTo: '#ffffff',
  },
  brazil: {
    name: 'Brazil',
    primary: '50 100% 50%',
    secondary: '140 80% 35%',
    accent: '220 80% 45%',
    gradientFrom: '#ffdf00',
    gradientTo: '#009c3b',
  },
  trinidad: {
    name: 'Trinidad',
    primary: '0 85% 50%',
    secondary: '0 0% 10%',
    accent: '0 0% 95%',
    gradientFrom: '#ce1126',
    gradientTo: '#000000',
  },
  barbados: {
    name: 'Barbados',
    primary: '220 90% 45%',
    secondary: '45 100% 50%',
    accent: '0 0% 10%',
    gradientFrom: '#00267f',
    gradientTo: '#ffc726',
  },
  ghana: {
    name: 'Ghana',
    primary: '50 100% 50%',
    secondary: '0 85% 40%',
    accent: '140 70% 30%',
    gradientFrom: '#fcd116',
    gradientTo: '#ce1126',
  },
  ireland: {
    name: 'Ireland',
    primary: '140 70% 35%',
    secondary: '30 90% 55%',
    accent: '0 0% 100%',
    gradientFrom: '#169b62',
    gradientTo: '#ff883e',
  },
  italy: {
    name: 'Italy',
    primary: '140 70% 35%',
    secondary: '0 80% 45%',
    accent: '0 0% 100%',
    gradientFrom: '#008c45',
    gradientTo: '#cd212a',
  },
  mexico: {
    name: 'Mexico',
    primary: '140 70% 30%',
    secondary: '0 85% 40%',
    accent: '0 0% 100%',
    gradientFrom: '#006847',
    gradientTo: '#ce1126',
  },
  japan: {
    name: 'Japan',
    primary: '0 80% 50%',
    secondary: '0 0% 95%',
    accent: '0 70% 45%',
    gradientFrom: '#bc002d',
    gradientTo: '#ffffff',
  },
  southafrica: {
    name: 'South Africa',
    primary: '140 80% 35%',
    secondary: '50 100% 50%',
    accent: '0 80% 45%',
    gradientFrom: '#007a4d',
    gradientTo: '#ffb612',
  },
  colombia: {
    name: 'Colombia',
    primary: '50 100% 50%',
    secondary: '220 80% 40%',
    accent: '0 80% 45%',
    gradientFrom: '#fcd116',
    gradientTo: '#003893',
  },
  india: {
    name: 'India',
    primary: '25 90% 55%',
    secondary: '140 70% 30%',
    accent: '220 70% 40%',
    gradientFrom: '#ff9933',
    gradientTo: '#138808',
  },
};

// Mode options
export const modes = {
  dark: {
    name: 'Dark',
    background: '240 10% 4%',
    foreground: '0 0% 98%',
    card: '240 10% 6%',
    cardForeground: '0 0% 98%',
    muted: '240 5% 15%',
    mutedForeground: '240 5% 65%',
    border: '240 5% 15%',
  },
  light: {
    name: 'Light',
    background: '0 0% 100%',
    foreground: '240 10% 4%',
    card: '0 0% 98%',
    cardForeground: '240 10% 4%',
    muted: '240 5% 92%',
    mutedForeground: '240 5% 40%',
    border: '240 5% 85%',
  },
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('dark');
  const [colorScheme, setColorScheme] = useState('blue');
  const [isLoading, setIsLoading] = useState(true);
  const overrideModeRef = useRef(null);

  // Load theme from localStorage on mount (before user data loads)
  useEffect(() => {
    const savedMode = localStorage.getItem('rollm8s_theme_mode');
    const savedColor = localStorage.getItem('rollm8s_theme_color');
    
    if (savedMode && modes[savedMode]) {
      setMode(savedMode);
    }
    if (savedColor && colorSchemes[savedColor]) {
      setColorScheme(savedColor);
    }
    setIsLoading(false);
  }, []);

  // Apply theme to CSS variables
  useEffect(() => {
    if (isLoading) return;
    // If a page override is active, skip applying the global mode
    if (overrideModeRef.current) return;
    
    const root = document.documentElement;
    const modeColors = modes[mode];
    const schemeColors = colorSchemes[colorScheme];

    // Apply mode colors
    root.style.setProperty('--background', modeColors.background);
    root.style.setProperty('--foreground', modeColors.foreground);
    root.style.setProperty('--card', modeColors.card);
    root.style.setProperty('--card-foreground', modeColors.cardForeground);
    root.style.setProperty('--muted', modeColors.muted);
    root.style.setProperty('--muted-foreground', modeColors.mutedForeground);
    root.style.setProperty('--border', modeColors.border);

    // Apply color scheme
    root.style.setProperty('--primary', schemeColors.primary);
    root.style.setProperty('--secondary', schemeColors.secondary);
    root.style.setProperty('--accent', schemeColors.accent);
    root.style.setProperty('--gradient-from', schemeColors.gradientFrom);
    root.style.setProperty('--gradient-to', schemeColors.gradientTo);

    // Compute logo hue rotation (logo is natively purple ~280°)
    const primaryHue = parseInt(schemeColors.primary.split(' ')[0], 10) || 280;
    const logoHueRotate = primaryHue - 280;
    root.style.setProperty('--logo-hue', `${logoHueRotate}deg`);

    // Update ALL browser theme-color meta tags to match the color scheme
    // Some browsers (Samsung Internet, etc.) use media-specific meta tags
    document.querySelectorAll('meta[name="theme-color"]').forEach(meta => {
      meta.setAttribute('content', schemeColors.gradientFrom);
    });

    // Paint the NATIVE Android status bar + navigation bar (Capacitor APK only).
    // On web/PWA this is a silent no-op — the meta tag above handles it.
    setNativeChromeColor(schemeColors.gradientFrom);

    // Update body class for mode
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(mode);

    // Save to localStorage
    localStorage.setItem('rollm8s_theme_mode', mode);
    localStorage.setItem('rollm8s_theme_color', colorScheme);
  }, [mode, colorScheme, isLoading]);

  // Load theme from user profile
  const loadUserTheme = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const user = response.data;
      if (user.theme_mode && modes[user.theme_mode]) {
        setMode(user.theme_mode);
      }
      if (user.theme_color && colorSchemes[user.theme_color]) {
        setColorScheme(user.theme_color);
      }
    } catch (error) {
      console.log('Could not load user theme preferences');
    }
  };

  // Save theme to user profile
  const saveThemeToProfile = async () => {
    try {
      const token = localStorage.getItem('rollm8s_token');
      if (!token) return;

      await axios.put(`${API}/users/me/theme`, 
        { theme_mode: mode, theme_color: colorScheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.log('Could not save theme preferences');
    }
  };

  const updateMode = (newMode) => {
    if (modes[newMode]) {
      setMode(newMode);
    }
  };

  // Page-level mode override — bypasses localStorage and context re-renders
  const setModeOverride = (overrideMode) => {
    overrideModeRef.current = overrideMode;
    const root = document.documentElement;
    const modeColors = modes[overrideMode];
    if (!modeColors) return;
    root.style.setProperty('--background', modeColors.background);
    root.style.setProperty('--foreground', modeColors.foreground);
    root.style.setProperty('--card', modeColors.card);
    root.style.setProperty('--card-foreground', modeColors.cardForeground);
    root.style.setProperty('--muted', modeColors.muted);
    root.style.setProperty('--muted-foreground', modeColors.mutedForeground);
    root.style.setProperty('--border', modeColors.border);
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(overrideMode);
  };

  const clearModeOverride = () => {
    overrideModeRef.current = null;
    // Re-apply the global mode
    const root = document.documentElement;
    const modeColors = modes[mode];
    root.style.setProperty('--background', modeColors.background);
    root.style.setProperty('--foreground', modeColors.foreground);
    root.style.setProperty('--card', modeColors.card);
    root.style.setProperty('--card-foreground', modeColors.cardForeground);
    root.style.setProperty('--muted', modeColors.muted);
    root.style.setProperty('--muted-foreground', modeColors.mutedForeground);
    root.style.setProperty('--border', modeColors.border);
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(mode);
  };

  const updateColorScheme = (newScheme) => {
    if (colorSchemes[newScheme]) {
      setColorScheme(newScheme);
    }
  };

  return (
    <ThemeContext.Provider value={{
      mode,
      colorScheme,
      modes,
      colorSchemes,
      updateMode,
      updateColorScheme,
      loadUserTheme,
      saveThemeToProfile,
      setModeOverride,
      clearModeOverride,
      isLoading,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
